import { FormGroupDirective, NgForm, UntypedFormControl } from '@angular/forms';
import { QuoteStoreService } from '@market-socket/quote-store.service';
import { CURRENT_PLATFORMTYPE, INDEXSYMBOL, Utils } from 'src/app/_helpers';
import { MutualFundDataResponse } from 'src/app/app.interfaces';
import { GTTOrderIdentifier, GTTOrderModeDisplay, GTTOrderTypeDisplay, GTTProductTypeDisplay, GTTStatus, GTTTriggerTypeDisplay, ReverseInstrumentType, SpotDerivativeMap, SuperBookOrderModeDisplay, SuperBookOrderTypeDisplay, SmartBookOrderValidityDisplay, SmartBookProductTypeDisplay, VendorType } from 'src/app/app.structs';
import {
    BidPriceStoreObj, BidPriceStoreObjI, BOCOInitInterface, ConfigParams, GTTOrderBookTable, HoldingObj, HoldingResponseObj, HoldingTable, LTPStoreObj, LTPStoreObjI, MarginSummaryList, MarginSummaryResponse, MarketSegmentSettingsResponse, MarketWatchQuote, MarketWatchTable, MtfHoldingResponseObj, MtfHoldingTable, OrderBookDataResponse, OrderBookTable, OrderDepthInterface, OrderDepthResponseInterface, OrderMessagesInterface, PlaceOrderRequest, PositionsObjResponse, PositionsTable, SingleIndexQuotes, SocketDepth, SuperBookSymbolData, SymbolRowRequest, TradeBookTable,
    TradesResponseDataInterface, SmartBookTable,
} from './app.interface';
import {
    AdvanceOrder,
    BOCOPriceType,
    BOCOProductTypes,
    BOCOStopModeTypes, BracketCoverOrderState,
    ContractType,
    DialogType,
    MarketKeywords,
    MarketWatchSymbolType,
    OrderCategory,
    OrderMode,
    OrderStatus,
    OrderType,
    PlatformType
} from './app.structs';
import { FilterOptions, SEARCH_SYMBOL_INSTRUMENT_TYPE } from './components/add-symbol/symbol-search';
import { cancelNotAllowedOrderStatus, GMT_IST_DIFFERENCE_SEC, modifyNotAllowedOrderStatus } from './helpers/shared-constants';
import { SharedUtils } from './helpers/shared.utilities';
import { ConfigParamsService } from './providers/config-params/config-params.service';
import { SymbolSearchService } from './providers/symbol-search/symbol-search.service';
import { MarketIDMap, QUOTE_UPDATE_TYPE } from './websocket.enums';


/**
 * #################################################################
 * ####################| User Book Models Start |###################
 * #################################################################
 */

// DEFAULT_MARKET will be used in holding-widget in case we do not recieve any update from the socket
// and when we square off a holding we are able to fetch symbol id for default market and it does not throw any server error
export let DEFAULT_MARKET: string = null;
export class OrderBook implements OrderBookTable {
    uAlgoOrderProduct: string[];
    /**
     * Definition of Properties is important
     * as Interfaces dont exist on Runtime.
     */
    client_order_id = null;
    original_client_order_id = '';
    exchange_order_id = '';
    order_status = null;
    order_mode = null;
    product_type = null;
    order_type = null;
    order_validity = null;
    quantity = 0;
    market_id = '';
    amount = 0;
    symbol = '';
    series = '';
    scrip_name = '';
    price = 0;
    instrument_type = '';
    sell_amount = 0;
    total_filled_qty = 0;
    last_filled_qty = 0;
    last_filled_price = 0;
    pending_qty = 0;
    created = 0;
    modified = 0;
    error_text = '';
    primary_client_code = '';
    gtd_date = 0;
    stop_price = 0;
    strategy_id = 0;
    trade_id = '';
    order_category = '';
    exchange_client_code = '';
    participant_code = '';
    is_trigerred = false;
    market_to_limit = false;
    fix_cl_order_id = '';
    placed_by = '';
    symbol_id = 0;
    security_id = 0;
    scrip_precision = 0;
    qty_precision = 0;
    expiry = null;
    expiry_date2 = null;
    option_type = null;
    strike_price = null;
    tick_size = 0;
    lot_size = 1;
    cancellationInProgress = false;
    holding_type = '';
    disclosed_quantity = 0;
    master_order_id = '';
    generated_order_id = null;
    contract_type = ContractType.single;
    // BOCO Fields
    bracket_cover_order_details: BOCODetailStruct;
    modified_by = '';
    last_trade_price = null;
    change_in_price = null;
    change_percent = null;
    isChecked = false;
    scheduled_order_time = null;
    formatted_symbol = '';
    type_identifier = null;
    order_identifier = '';
    is_mtf_enabled = false;
    platform_type = '';
    
    static fromTradeBook(row: TradesResponseDataInterface) {
        const order = new OrderBook();
        order.client_order_id = row.client_order_id;
        order.exchange_order_id = row.exchange_order_id;
        order.trade_id = row.trade_id;
        order.market_id = row.market_id;
        order.security_id = row.security_id;
        order.instrument_type = row.instrument_type;
        order.product_type = row.product_type;
        order.scrip_name = row.scrip_name;
        order.symbol = row.symbol;
        order.formatted_symbol = row.formatted_symbol;
        order.series = row.series;
        order.order_mode = row.side;
        order.last_filled_qty = row.last_filled_qty;
        order.last_filled_price = row.last_filled_price;
        order.quantity = row.quantity;
        order.price = row.last_filled_price;
        order.modified = row.modified;
        order.scrip_precision = row.scrip_precision;
        order.order_category = row.order_category;
        order.strategy_id = row.strategy_id;
        order.order_status = row.order_status;
        order.primary_client_code = row.primary_client_code;
        order.participant_code = row.participant_code;
        order.exchange_client_code = '' + row.exchange_client_code;
        order.order_validity = row.order_validity;
        order.fix_cl_order_id = row.fix_cl_order_id;
        order.placed_by = row.placedBy;
        order.lot_size = row.lot_size;
        order.symbol_id = row.symbol_id;
        order.master_order_id = row.master_order_id.toString();
        order.modified_by = row.modified_by;

        return order;
    }

    constructor(orderBookResponse?: OrderBookDataResponse, _config?: ConfigParamsService) {
        if (!orderBookResponse) { return; }
        //Checking if Exchange is BSE then converting its time to seconds
        const checkExchange = orderBookResponse["market_id"].includes("BSE");
        if (checkExchange) {
            orderBookResponse["modified"] = Math.round(SharedUtils._convertDateTime(orderBookResponse['modified']) * 0.001);
        }

        Object.keys(this).forEach(key => {
            this[key] = orderBookResponse[key];
        });
        // if (orderBookResponse.expiry_date) {
        //     this.expiry = orderBookResponse.expiry_date;
        // }
        this.isChecked = false;
        if (!this['qtyPrecision']) {
            this['qtyPrecision'] = this['qty_precision'] = 0;
        }
        _config.getConfigParams().then((config_params: ConfigParams) => {
            if (config_params) {
                this.uAlgoOrderProduct = config_params.uAlgo;
            }
        });
        this.bracket_cover_order_details = orderBookResponse.bracket_cover_order_details;
        if (!orderBookResponse.formatted_symbol) {
            this.formatted_symbol = '';
        }
        if (this.type_identifier !== null && this.type_identifier !== undefined) {
            this.order_identifier = GTTOrderIdentifier[this.type_identifier];
        }
    }

    updateOrder(orderBookResponse: OrderBookDataResponse) {
        Object.keys(this).forEach(key => {
            // Checking if key is scheduled_order_time then updating it as it is.
            if (key === 'scheduled_order_time') {
                this[key] = orderBookResponse[key];
            } else {
                this[key] = !SharedUtils.isNullUndefined(orderBookResponse[key]) ? orderBookResponse[key] : this[key];
            }
        });

        this.isChecked = false;
    }

    get isBoco() {
        return BOCOProductTypes.includes(this.product_type) && this.bracket_cover_order_details;
    }
    get hideStopLoss() {
        return this.order_status !== OrderStatus.filled || this.product_type === AdvanceOrder.BO ||
            this.product_type === AdvanceOrder.CO || this.order_category === OrderCategory.amo ||
            this.uAlgoOrderProduct && this.uAlgoOrderProduct.includes(this.product_type);
    }

    get hideuAlgo() {
        return this.uAlgoOrderProduct && this.uAlgoOrderProduct.includes(this.product_type);
    }

    get isCancelAllowed(): boolean {
        try {
            const isNormalOrderCancelable = !cancelNotAllowedOrderStatus.includes(this.order_status);
            const isBocoCancelable = this.isBoco ?
                this.bracket_cover_order_details.order_state !== BracketCoverOrderState.OrderState_Exit : true;
            return isNormalOrderCancelable && isBocoCancelable &&  (this.platform_type.toLowerCase() !== PlatformType.DMA);
        } catch (err) {
            console.error(`Error in Cancel allowed getter in order book. ${err}`);
            return false;
        }
    }

    get isModifyAllowed(): boolean {
        try {
            const isNormalOrderModifiable = !modifyNotAllowedOrderStatus.includes(this.order_status) && this.order_category != OrderCategory.auction;
            return isNormalOrderModifiable &&  (this.platform_type.toLowerCase() !== PlatformType.DMA )&& !SharedUtils.checkForAutoSquareOffOrder(this.strategy_id);
        } catch (err) {
            console.error(`Error in Cancel allowed getter in order book. ${err}`);
            return false;
        }
    }

    get isBOCOStopLossOrder() {
        try {
            return this.isBoco && (this.order_type === OrderType['Stop Loss Market']);
        } catch (err) {
            console.error(`Error in isBOCOStopLossOrder getter in order book. ${err}`);
            return false;
        }
    }

    updateQuote(quote: MarketWatchQuote) {
        if (quote.last_trade_price) {
            this.last_trade_price = quote.last_trade_price;
        }
        if (quote.close_price) {
            this.change_in_price = quote.last_trade_price - quote.close_price;
            this.change_percent = (this.change_in_price / quote.close_price) * 100;
        }
    }
}

export class Holding implements HoldingTable {
    isin = null;
    symbol = null;
    total_qty = null;
    pending_qty = null;
    sold_qty = null;
    close_price_value = null;
    investment_value = null;
    current_value = null;
    profit_loss = null;
    client_id = null;
    precision = null;
    scrip_precision = null;
    tick_size = null;
    open_qty = null;
    collateral_qty = null;
    haircut = null;
    t1_qty = null;
    enable_t1_qty = false;
    ltp_available = true;
    calculation_ltp = 0;
    collatralCalculationField = 'close_price_value';
    last_trade_price = null;
    change_in_price = null;
    change_percent = null;
    day_pl = null;
    average_price = null;
    is_edis_done = false;
    pledgable = false;
    quantity_to_pledge = 0;
    pledge_check = true;
    error = false;
    edis_auth_qty = 0;
    edis_cancel_qty = 0;
    selected_auth_quantity = 1;
    transactions = [];
    auth_checked = false;
    unauth_checked = false;
    day_pl_percent = null;
    pl_percent = null;
    series = null;
    cuspa_quantity = 0;
    formatted_symbol = '';
    security_id = null;
    market_id = null;
    is_mtf_enabled = false;

    constructor(holding: HoldingObj, enable_t1_holding = false, useLtp = false) {
        this.updateHolding(holding);

        this.enable_t1_qty = enable_t1_holding;
        this.collatralCalculationField = useLtp && this.ltp_available ? 'calculation_ltp' : 'close_price_value';
    }

    updateQuote(last_trade_price: number, close_price: number, holdingStore: HoldingStore) {
        if (last_trade_price && close_price) {
            this.change_in_price = last_trade_price - close_price;
            this.change_percent = (this.change_in_price / close_price) * 100;
        }
        this.last_trade_price = last_trade_price;
        this.close_price_value = close_price;
        const LTPObject = holdingStore.ltpStore[this.isin];
        const ltp = LTPObject.ltp.get(LTPObject.prioritySymbolId);
        this.current_value = this.available * ltp;
        this.profit_loss = this.current_value - this.investment_value;
        this.day_pl = (ltp - this.close_price_value) * this.available;
        this.day_pl_percent = this.close_price_value ? (this.day_pl / (this.close_price_value * this.available)) * 100 : this.close_price_value;
        this.pl_percent = this.investment_value ? (this.profit_loss / this.investment_value) * 100 : this.investment_value;
    }

    updateHolding(holding: HoldingObj) {
        Object.keys(this).forEach(key => {
            if (holding[key] !== undefined) {
                this[key] = holding[key];
            }
        });
        this.average_price = (this.average_price === 0) ? null : this.average_price;
        this.investment_value = (this.average_price === null) ? null : (this.average_price * this.available);
        this.open_qty = this.total_qty - this.sold_qty;
        this.quantity_to_pledge = this.outStandingQty - this.collateral_qty - this.cuspa_quantity;
        // RE-796 As per requirement after DMS reconnection, updating edis_auth_qty again which goserver used to send in API Call.
        this.edis_auth_qty = this.edis_auth_qty - (this.sold_qty + this.pending_qty);
        this.edis_auth_qty < 0 && (this.edis_auth_qty = 0);
    }

    get available() {
        return (this.total_qty - this.sold_qty);
    }

    get quantity_that_can_be_pledged() {
        return this.outStandingQty - this.collateral_qty - this.cuspa_quantity;
    }

    get quantity_that_can_be_pledged_without_cuspa() {
        return this.outStandingQty - this.collateral_qty;
    }

    get pledge_error() {
        return this.pledge_check && (this.quantity_to_pledge <= 0 ||
            (this.quantity_to_pledge > (this.quantity_that_can_be_pledged)));
    }

    get warning_border_class() {
        return (!+this.selected_auth_quantity || +this.selected_auth_quantity > this.total_qty) && this.auth_checked;
    }

    get collateralValue(): number | string {
        // formula updated as per requirement RE-3222
        const totalValue = Math.min((this.total_qty - this.sold_qty), this.collateral_qty);
        if (totalValue < 0) {
            return 0;
        }

        /**
         * Original formula is :-
         *     const collateralValueNum = ((totalValue * this.close_price_value) / 100) * (100 - (this.haircut / 100));
         *
         * But to avoid precesion loss (In JS multiplication) will be using normalised formula.
         *
         * i.e. => ((a * b)/ 100) * (100 - (c/100)) == ((a * b) * (10000 - c)) / 10000
         */

        const collateralValueNum = ((totalValue * this[this.collatralCalculationField]) * (10000 - this.haircut)) / 10000;
        return this.truncate(collateralValueNum, this.scrip_precision);
    }

    get avilableQty(): number {
        return this.enable_t1_qty ? this.total_qty : this.total_qty - this.t1_qty;
    }

    get outStandingQty(): number {
        return this.avilableQty - this.sold_qty - this.pending_qty;
    }

    /**
     * Method to truncate a number to specified precision without rounding off the number.
     * toFixed rounds off value in chrome.
     *
     * @param value Value to be truncated
     * @param precision Precision to be truncated to.
     * @returns Truncated Value
     */
    truncate(value: number, precision: number): number {
        const multiplier = Math.pow(10, precision),
            adjustednum = value * multiplier,
            tuncatedNumber = Math.floor(adjustednum);

        return tuncatedNumber / multiplier;

    }
}

export class MtfHolding implements MtfHoldingTable {
    applicable_margin: number = 0;
    average_price: number = 0;
    client_id: string = '';
    formatted_symbol: string = '';
    isin: string = '';
    qty: number = 0;
    scrip_precision: number = 0;
    security_id: number = 0;
    sold_qty: number = 0;
    investment_value: number = 0;
    fundedValue: number = 0;
    presentValue: number = 0;
    market_id: string = '';
    balance_qty: number = 0;
    current_value: number = 0;
    profit_loss: number = 24;
    day_pl: number = 0;
    day_pl_percent: number = 0;
    pl_percent: number = 0;
    last_trade_price: number = 0;
    close_price_value: number = 0;
    change_in_price: number = 0;
    change_percent: number = 0;
    fundedValue_percent: number = 0;
    applicable_percentage: number = 0;

    constructor(holding: MtfHoldingTable) {
        this.updateValues(holding);
    }

    updateValues(holding: MtfHoldingTable) {
        Object.keys(this).forEach(key => {
            if (holding[key] !== undefined) {
                this[key] = holding[key];   
            }
        });
        this.average_price = SharedUtils.applyPrecision((this.average_price / this.qty), this.scrip_precision);
        this.applicable_margin = SharedUtils.applyPrecision(this.applicable_margin, this.scrip_precision);
        this.balance_qty = this.qty;
        this.investment_value = this.average_price * this.balance_qty; //buy value
        this.fundedValue = this.balance_qty ? this.investment_value - this.applicable_margin : 0;
        this.fundedValue_percent = this.balance_qty ? (this.fundedValue / this.investment_value) * 100 : null;
    }

    updateValuesStream(holding: MtfHoldingTable) {
        Object.keys(this).forEach(key => {
            if (holding[key] !== undefined) {
                this[key] = holding[key];   
            }
        });
        this.average_price = SharedUtils.applyPrecision((this.average_price / this.qty), this.scrip_precision);
        this.balance_qty = this.qty;
        this.investment_value = this.average_price * this.balance_qty; //buy value
        this.fundedValue =  this.balance_qty ? this.investment_value - this.applicable_margin : 0;
        this.fundedValue_percent = this.balance_qty ? (this.fundedValue / this.investment_value) * 100 : null;
    }

    updateMargin(margin: number , applicable_percentage: number, scrip_precision: number) {
        this.applicable_margin = SharedUtils.applyPrecision(margin, scrip_precision);
        this.applicable_percentage = applicable_percentage;
        this.fundedValue = this.investment_value - this.applicable_margin;
        this.fundedValue_percent = (this.fundedValue / this.investment_value) * 100;
    }

    updateQuote(last_trade_price: number, close_price: number, holdingStore: HoldingStore) {
        this.last_trade_price = last_trade_price;
        this.close_price_value = close_price;
        this.current_value = this.balance_qty * (last_trade_price ? last_trade_price : close_price);
        this.profit_loss = this.current_value - this.investment_value;
        this.day_pl = (last_trade_price - close_price) * this.balance_qty;
        this.day_pl_percent = this.close_price_value ? (this.day_pl / (this.close_price_value * this.balance_qty)) * 100 : this.close_price_value;
        this.pl_percent = this.investment_value ? (this.profit_loss / this.investment_value) * 100 : this.investment_value;
        this.change_in_price = last_trade_price - close_price;
        this.change_percent = this.close_price_value ? (this.change_in_price / this.close_price_value) * 100 : this.close_price_value;
    }
}

export class HoldingStore {
    // Bid Price store for picking default market for selling holding.
    bidPriceStore: BidPriceStoreObjI = {};

    // Quote Store
    quoteStore: Map<number, MarketWatchQuote> = new Map<number, MarketWatchQuote>();

    /**
     * Ltp store needed for case in which multiple markets. We need to update ltp of all markets.
     */
    ltpStore: LTPStoreObjI = {};
    mtfLtpStore = {};

    // Symbol ID to ISIN Map.
    symbolIdToISIN = new Map<number, string>();
    securityToISIN = new Map<string, string>();
    symbolDetailsMap = new Map<number, {}>();
    mtfSymbolDetailsMap = new Map<string, {}>();

    // isinHoldingMap Map : Isin -> Holding
    isinHoldingMap: Map<string, Holding> = new Map();
    mtfIsinHoldingMap: Map<string, MtfHolding> = new Map();
    /**
     * Utility for getting max LTP for a isin.
     */
    getPriorityLTP(isin: string): number {
        const LTPObject = this.ltpStore[isin];
        return LTPObject.ltp.get(LTPObject.prioritySymbolId);
    }

    /**
     * Method to parse and return holding response list.
     *
     * @param holdings Holding List Response
     * @param params Config Param for t1 holding and LTP calculation/
     * @returns List of parsed Holding.
     */
    addHoldings(holdings: HoldingResponseObj, params: ConfigParams): Holding[] {
        return Object.values(holdings).map((value) => {
            const bidPriceObj: BidPriceStoreObj = {
                bidPrice: new Map<number, number>()
            };
            let holding;
            if (params == undefined) {
                holding = new Holding(value);
            }
            else {
                holding = new Holding(value, params.enable_t1_holdings_sell, params.use_ltp_for_collateral);
            }
            const ltpObj: LTPStoreObj = {
                ltp: new Map<number, number>(),
                prioritySymbolId: null
            };

            const backendDefaultMarket = value.default_market;
            let selectedMarket = Object.keys(value.markets)[0];

            if (value.markets[backendDefaultMarket]) {
                selectedMarket = backendDefaultMarket;
            }

            const symbolIdN = +value.markets[selectedMarket];
            const securityId = value.security_ids[selectedMarket][0];
            if (!DEFAULT_MARKET) {
                DEFAULT_MARKET = value.default_market;
            }
            const data = {
                symbol_id: symbolIdN,
                market_id: selectedMarket,
                security_id: securityId,
                scrip_precision: value.scrip_precision,
                isin: holding.isin
            }
            this.symbolDetailsMap.set(symbolIdN, data);
            this.symbolIdToISIN.set(symbolIdN, holding.isin);
            bidPriceObj.bidPrice.set(symbolIdN, 0);
            ltpObj.ltp.set(symbolIdN, 0);
            ltpObj.prioritySymbolId = symbolIdN;
            this.ltpStore[value.isin] = ltpObj;
            this.bidPriceStore[value.isin] = bidPriceObj;
            this.isinHoldingMap.set(value.isin, holding);
            return holding;
        });
    }

    addMTFHoldings(holdings: MtfHoldingResponseObj): MtfHolding[] {
        return Object.values(holdings).map((value) => {
            const holding = new MtfHolding(value);
            const ltpObj  = {
                ltp: new Map<string, number>() ,
                prioritySymbolId: null
            }
            const data = {
                symbol_id: null,
                market_id: holding.market_id,
                security_id: holding.security_id,
                scrip_precision: holding.scrip_precision,
                isin: holding.isin
            }
            this.mtfSymbolDetailsMap.set(Utils.getUniqueKey(holding.market_id, holding.security_id), data);
            this.securityToISIN.set(Utils.getUniqueKey(holding.market_id, holding.security_id), holding.isin);
            ltpObj.ltp.set(Utils.getUniqueKey(holding.market_id, holding.security_id), 0);
            ltpObj.prioritySymbolId = null;
            this.mtfLtpStore[holding.isin] = ltpObj;
            this.mtfIsinHoldingMap.set(value.isin, holding);
            return holding;
        });
    }

    /**
     * Method to update LTP and BidPrice of ISINs.
     *
     * @param quote Quote Packet
     */
    updateQuote(quote: MarketWatchQuote) {
        const isin = this.symbolIdToISIN.get(quote.symbol_id);
        const data = this.symbolDetailsMap.get(quote.symbol_id);
        if (data && data['isin']) {
            this.quoteStore.set(quote.symbol_id, quote);

            const BidPriceObject = this.bidPriceStore[data['isin']];
            BidPriceObject.bidPrice.set(quote.symbol_id, quote.bid_price);
            this.bidPriceStore[data['isin']] = BidPriceObject;

            const LTPObject = this.ltpStore[data['isin']];
            LTPObject.ltp.set(quote.symbol_id, quote.last_trade_price);
            this.ltpStore[data['isin']] = LTPObject;
        }
    }

    /**
     * Method to calculate current value of a holding.
     *
     * @param holding Holding to calculate Current Price of.
     * @returns Current Price
     */
    getCurrentValue(holding: Holding): number {
        const ltp = this.getPriorityLTP(holding.isin);
        // For avoiding null values on fetching data
        holding.current_value = holding.available * ltp;
        return holding.available * ltp;
    }

    getDayPLPercent(holding: Holding): number {
        const investmentValue = holding.close_price_value * holding.total_qty;
        return investmentValue ? (holding.day_pl / investmentValue) * 100 : investmentValue;
    }

    getPLPercent(holding: Holding): number {
        return holding.investment_value ? (this.getPL(holding) / holding.investment_value) * 100 : holding.investment_value;
    }

    getPL(holding: Holding) {
        // For avoiding null values on fetching data
        holding.profit_loss = this.getCurrentValue(holding) - holding.investment_value;
        return holding.profit_loss;
    }
}



// Trade Book Class

export class TradeBook implements TradeBookTable {
    uAlgoTradeProduct: string[];
    /**
     * Definition of Properties is important
     * as Interfaces dont exist on Runtime.
     */
    bracket_cover_order_details?: BOCODetailStruct;
    client_order_id = 0;
    security_id = 0;
    exchange_order_id = '';
    trade_id = '';
    market_id = '';
    instrument_type = '';
    product_type = null;
    scrip_name = '';
    symbol = '';
    series = '';
    side = null;
    last_filled_qty = 0;
    total_filled_qty = 0;
    last_filled_price = 0;
    last_traded_amount = 0;
    modified = 0;
    order_category = '';
    scrip_precision = 0;
    strategy_id = 0;
    primary_client_code = '';
    participant_code = '';
    order_status = '';
    exchange_client_code = 0;
    order_validity = null;
    fix_cl_order_id = '';
    clientId = '';
    conf_receive_time = 0;
    placed_by = '';
    scripPrecision = 0;
    qtyPrecision = 0;
    lot_size = 0;
    symbol_id = 0;
    generated_order_id = null;
    master_order_id = 0;
    tick_size = 5;
    expiry_date = null;
    strike_price = 0;
    modified_by = '';
    last_trade_price = null;
    quantity = 0;
    change_in_price = null;
    change_percent = null;
    formatted_symbol = '';
    order_type = OrderType.limit;
    type_identifier = null;
    order_identifier = '';
    option_type = null;
    expiry = null;
    is_mtf_enabled = false;
    contract_type = '';

    constructor(tradeBookResponse: TradesResponseDataInterface, _config: ConfigParamsService) {
        //Checking if Exchange is BSE then converting its time to seconds
        const checkExchange = tradeBookResponse["market_id"].includes("BSE");
        if (checkExchange) {
            tradeBookResponse["modified"] = Math.round(SharedUtils._convertDateTime(tradeBookResponse['modified']) * 0.001);
        }
        Object.keys(this).forEach(key => {
            if (tradeBookResponse[key]) {
                this[key] = tradeBookResponse[key];
            }
        });
        // if (tradeBookResponse.expiry) {
        //     this.expiry_date = tradeBookResponse.expiry;
        // }
        // this.modified = Utils._convertDateTime(tradeBookResponse.modified);
        _config.getConfigParams().then((config_params: ConfigParams) => {
            if (config_params) {
                this.uAlgoTradeProduct = config_params.uAlgo;
            }
        });
        this.bracket_cover_order_details = tradeBookResponse.bracket_cover_order_details;
        if (this.type_identifier !== null && this.type_identifier !== undefined) {
            this.order_identifier = GTTOrderIdentifier[this.type_identifier];
        }
    }

  
    get hideStopLoss() {
        return this.order_status !== OrderStatus.filled || this.product_type === AdvanceOrder.BO ||
            this.product_type === AdvanceOrder.CO || this.order_category === OrderCategory.amo ||
            this.uAlgoTradeProduct && this.uAlgoTradeProduct.includes(this.product_type);
    }

    get isBoco() {
        return BOCOProductTypes.includes(this.product_type) && this.bracket_cover_order_details;
    }

    updateQuote(quote: MarketWatchQuote) {
        if (quote.last_trade_price) {
            this.last_trade_price = quote.last_trade_price;
        }
        if (quote.close_price) {
            this.change_in_price = quote.last_trade_price - quote.close_price;
            this.change_percent = (this.change_in_price / quote.close_price) * 100;
        }
    }

}

export class GTTBook implements GTTOrderBookTable {
    client_id = '';
    master_order_id = 0;
    market_id = '';
    side = 0;
    transaction_type = 0;
    product_type = 0;
    trigger_type = 0;
    order_type = 0;
    security_id = 0;
    platform = '';
    scheduled_time = 0;
    target_price = 0;
    target_quantity = 0;
    target_trigger_price = 0;
    sl_price?: number;
    sl_quantity?: number;
    sl_trigger_price?: number;
    isin: string;
    error_code?: string | null;
    error?: string | null;
    status = 0;
    status_text = '';
    created_at = 0;
    updated_at = 0;
    last_trade_price = 0;
    symbol_id = null;
    cancellationInProgress = false;
    symbol_data: any = {};
    formatted_symbol = '';
    scrip_precision = 0;
    trigger_type_display = '';
    order_mode = '';
    product_type_display = '';
    change_percent = 0;
    change_in_price = 0;
    isGttRepeat = false;
    dialogType: DialogType;
    isGttModify = false;
    order_type_display = '';
    tick_size = 0;
    id = 0;
    contract_type = '';
    ltp_created_at = 0;
    lot_size = 0;
    instrument_type = '';
    expiry = '';
    unique_identifier = '';
    is_triggered = false;
    symbol = '';
    is_mtf_enabled = false;

    constructor(order?: GTTOrderBookTable) {
        if (!order) return;
        //   Object.assign(this, order);
        Object.keys(this).forEach(key => {
            if (order[key] !== undefined) {
                this[key] = order[key];
            }
        });

        // let expiry = order.expiry || order.expiry_date;
        // if (expiry) {
        //     this.expiry = expiry;
        // }

        // Map unique_identifier to isin only if isin is not present
        if (order.unique_identifier && !this.isin) {
            this.isin = order.unique_identifier;
        }
        if(this.symbol_data) {
            this.symbol_id = this.symbol_data.SymbolID;
            this.formatted_symbol = this.symbol_data.FormattedSymbol;
            this.scrip_precision = this.symbol_data.ScripPrecision;
            this.tick_size = this.symbol_data.TickSize;
            this.contract_type = this.symbol_data.ContraceType;
            this.lot_size = this.symbol_data.MarketLot;
            this.instrument_type = ReverseInstrumentType[this.symbol_data.InstrumentType];
            this.expiry = this.symbol_data.Expiry;
            this.symbol = this.symbol_data.Symbol;
        }
        this.trigger_type_display = GTTTriggerTypeDisplay[this.trigger_type];
        this.order_mode = GTTOrderModeDisplay[this.side];
        this.product_type_display = GTTProductTypeDisplay[this.product_type];
        this.order_type_display = GTTOrderTypeDisplay[this.order_type];
    }

    get isModifyAllowed() {
        return this.status_text === GTTStatus.Open;
    }

    get isCancelAllowed() {
        return this.status_text === GTTStatus.Open;
    }

    updateQuote(quote: MarketWatchQuote) {
        if (quote.last_trade_price && quote.last_trade_price !== undefined) {
            this.last_trade_price = quote.last_trade_price;
        }
        if (quote.close_price) {
            this.change_in_price = quote.last_trade_price - quote.close_price;
            this.change_percent = (this.change_in_price / quote.close_price) * 100;
        }
    }
}

export class SuperBook implements SmartBookTable {
    order_id = BigInt(0);  // Change from number to bigint
    order_id_string = "";
    smart_order_ref = "";
    client_id = "";
    smart_order_type = 0;
    security_id = 0;
    exchange_id = 0;
    order_mode = 0;
    order_type = 0;
    order_validity = 0;
    total_quantity = 0;
    disclosed_quantity = 0;
    status = 0;
    start_time = 0;
    end_time = 0;
    execution_method = 0;
    limit_price = 0;
    market_lot = 0;
    product_type = 0;
    twap_config: any = null;
    iceberg_config: any = null;
    created_at = 0;
    updated_at = 0;
    super_boco_config: any = null;
    pov_config: any = null;
    set_custom_expiry = false;
    expiry_date = 0;
    strategy_state: any = null;
    calculated_values: any = null;
    last_trade_price = 0;
    change_in_price = 0;
    change_percent = 0;
    order_mode_display = '';
    product_type_display = '';
    market_id = '';
    ltp_key = '';
    lot_size = 1;
    symbol_id = null;
    symbol_data: SuperBookSymbolData = null;
    orderValidity?: string = ''
    instrument_type?: string = '';
    current_trail_price = 0;
    isSmartRepeat = false;
    isSmartModify = false;
    dialogType: DialogType;
    symbol = '';
    expiry = '';

    constructor(superBook: SmartBookTable, ) {
        Object.keys(this).forEach(key => {
            if (superBook[key]) {
                this[key] = superBook[key];
            }
        });
        
        // Fix: Ensure exchange_id exists before using it
        if (this.exchange_id !== undefined && this.exchange_id !== null) {
            this.market_id = MarketIDMap[this.exchange_id];
        } else {
            // Fallback: try to get market_id from the original data
            this.market_id = superBook?.['market_id'] || '';
        }
        
        this.order_mode_display = SuperBookOrderModeDisplay[this.order_mode];
        this.product_type_display = SmartBookProductTypeDisplay[this.product_type];
        this.ltp_key = Utils.getUniqueKey(this.market_id, this.security_id);
        this.orderValidity = SmartBookOrderValidityDisplay[superBook?.['order_validity']];
        this.symbol_data = {
            formatted_symbol: superBook?.['symbol_details']?.['FormattedSymbol'],
            scrip_precision: superBook?.['symbol_details']?.['ScripPrecision'],
            symbol_id: superBook?.['symbol_details']?.['SymbolID'],
            tick_size: superBook?.['symbol_details']?.['TickSize'],
        }
        this.expiry = superBook?.['symbol_details']?.['Expiry'];
        this.symbol = superBook?.['symbol_details']?.['Symbol'];
        this.lot_size= superBook?.['symbol_details']?.['LotSize'],
        this.instrument_type = ReverseInstrumentType[superBook?.['symbol_details']?.['InstrumentType']];
        this.order_type =  SuperBookOrderTypeDisplay[superBook?.['order_type']];
        this.current_trail_price = superBook?.calculated_values?.current_trail_price;
        this.calculated_values=superBook?.['calculated_values'];
    }

    updateQuote(quote: MarketWatchQuote) {
        if (quote.last_trade_price && quote.last_trade_price !== undefined) {
            this.last_trade_price = quote.last_trade_price;
        }
        if (quote.close_price) {
            this.change_in_price = quote.last_trade_price - quote.close_price;
            this.change_percent = (this.change_in_price / quote.close_price) * 100;
        }
    }
}

export class Positions implements PositionsTable {
    uAlgoPositionProduct: string[];
    /**
     * Definition of Properties is important
     * as Interfaces dont exist on Runtime.
     */
    market_id = '';
    instrument_type = '';
    product_type = null;
    scrip_name = '';
    series = '';
    buy_qty = 0;
    buy_price = 0;
    buy_amount = 0;
    sell_qty = 0;
    sell_price = 0;
    sell_amount = 0;
    primary_client_code = '';
    exchange_client_code = '';
    buy_lots = 0;
    sell_lots = 0;
    open_qty = 0;
    netQuantity = 0;
    net_lots = 0;
    net_price = 0;
    net_value = 0;
    last_trade_price = 0;
    old_qty = 0;
    m2m = 0;
    settlement_amount = 0;
    booked_pl = 0;
    net_profit_loss = 0;
    scrip_precision = 0;
    netQty = 0;
    qtyPrecision = 0;
    security_id = 0;
    symbol_id = null;
    expiry_date = null;
    expiry = null;
    option_mode = null;
    strike_price = 0;
    tick_size = 1;
    lot_size = 1;
    isBoco = false;
    postion_row_key = null;
    trade_value = 1;
    top_bid_price = 0;
    top_ask_price = 0;
    dealer_id = 0;
    isChecked = false;
    close_price = 0;
    change_in_price = null;
    change_percent = null;
    dayPnl: number = 0;
    symbol: string = '';
    day_m2m = 0;
    net_profit_loss_percentage = 0;
    //dayPnlPercentage = 0; neeeded in future
    //bookedPnlPercentage = 0; neeeded in future
    applyClosePriceForMtm = false;
    applyClosePriceForBpl = false;
    formatted_symbol = '';
    is_mtf_enabled = false;
    contract_type = '';

    constructor(positionsResponse: PositionsObjResponse, _config: ConfigParamsService) {
        if (positionsResponse) {
            Object.keys(this).forEach(key => {
                if (positionsResponse[key]) {
                    this[key] = positionsResponse[key];
                }
            });

            // if (positionsResponse.expiry) {
            //     this.expiry_date = positionsResponse.expiry;
            // }

            if (this.isChecked !== undefined) {
                this.isChecked = positionsResponse.isChecked;
            }

            this.postion_row_key = `${this.symbol_id}_${this.product_type}`;
            this.netQuantity = this.open_qty;

            this.buy_lots = Math.abs(this.buy_qty / this.lot_size);
            this.sell_lots = Math.abs(this.sell_qty / this.lot_size);

            this.net_lots = this.buy_lots - this.sell_lots;
            this.isBoco = BOCOProductTypes.includes(this.product_type);
        }

        this.getConfigParams(_config);
        
    }

    async getConfigParams(_config: ConfigParamsService) {
        await _config.getConfigParams().then((config_params: ConfigParams) => {
            if (config_params) {
                this.uAlgoPositionProduct = config_params.uAlgo;
                //apply close price for future only as same check is at backend
                if (this.instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE) {
                    this.applyClosePriceForBpl = config_params.use_close_price_for_bpl_futures;
                    if (this.applyClosePriceForBpl) {
                        this.calculateBookedPnl();
                    }
                }
                this.applyClosePriceForMtm = config_params.use_close_price_for_mtm_calculation;
            }
        });
    }


    /**
     * Creates a new Positions instance and initializes its config parameters
     * @param positionsResponse The positions data to initialize the instance with
     * @param _config Configuration service to get parameters from
     * @returns A Promise that resolves to the initialized Positions instance
     */
    static async create(positionsResponse: PositionsObjResponse, _config: ConfigParamsService) {
        const position = new Positions(positionsResponse, _config);
        await position.getConfigParams(_config);
        return position;
    }


    get isProductConversionAllowed() {
        return !this.isBoco && (this.uAlgoPositionProduct && !this.uAlgoPositionProduct.includes(this.product_type));
    }

    get isAllowedMarket() {
        return this.market_id === MarketKeywords.NSECM || this.market_id === MarketKeywords.BSECM;
    }

    get hideuAlgo() {
        return this.uAlgoPositionProduct && this.uAlgoPositionProduct.includes(this.product_type);
    }


    /**
     * calculate day pnl for future and option
     * if old_qty is present then calculate m2m against close price
     * if buy_qty or sell_qty is present then calculate m2m against avg price
     */

    getDayProfitLoss() {
        let dayM2m = 0;

        /**
         * checking for both openqty an oldqty because
         * in case old qty is present it will not give clariity if the quantity is opened
         * or not 
         * Eg:
         * old qty = -2
         * buy qty = 2
         * sell qty = 0
         * today's open qty = 2
         * overall open qty = 0 (as -2 + 2 = 0)
         * for this case old qty is completely closed
         */
        if (this.old_qty && this.open_qty) {
            dayM2m += this.calculateM2mAgainstClosePrice();
        }
        if (this.open_qty) {
            dayM2m += this.calculateM2mAgainstAvgPrice();
        }

        if (this.market_id === MarketKeywords.MCX) {
            dayM2m = dayM2m * this.trade_value;
        }

        //this.dayPnlPercentage = pnlPercentageDenominator ? (bookedPL / pnlPercentageDenominator) * 100 : 0;
        this.day_m2m = dayM2m;
        this.dayPnl = this.booked_pl + dayM2m;
        this.dayPnl
    }

    /**
     * Calculates mark-to-market value against close price for positions with old quantity
     * 
     * For long positions (old_qty > 0):
     * m2m = (last_trade_price - close_price) * old_qty
     * 
     * For short positions (old_qty < 0):
     * m2m = (close_price - last_trade_price) * abs(old_qty)
     * 
     * @returns The calculated mark-to-market value
     */
    calculateM2mAgainstClosePrice() {
        let m2m = 0;
        let buyQty = this.buy_qty;
        let sellQty = Math.abs(this.sell_qty);

        if (this.old_qty) {
            if (this.old_qty > 0) {
                buyQty = this.buy_qty - this.old_qty;
            }
            else {
                sellQty = Math.abs(this.sell_qty) - Math.abs(this.old_qty);
            }
        }

        let openQty = buyQty - sellQty;

        let updatedOldQty = 0;


        /**
         * updatedOldQty is used to calculate m2m for old qty
         * olqQty  buyQty  SellQty  openQty(new)  openQty(old + new) updatedOldQty
         * 3       3       4        -1            2                  2
         * 1       0       1        -1            0                  0
         * -3      4       3        1            -2                 -2
         * 1       1       0        1             2                  1
         * 3       0       4        -4           -1                 0
         * -3      4       0        4             1                  0
         * -3      0       3        -3           -6                  0
         * 4       3       4        -1            3                  3
         * 4       3       9        -6           -2                  0
         * 1       1       1         0           1                   1
         * 5       4       0         4           9                   5
         * -9      3       4        -1          -10                 -9
         */

        if (this.old_qty * openQty > 0) {
            // Both have the same sign, old quantity remains
            updatedOldQty = this.old_qty;
        } else if (Math.abs(this.old_qty) >= Math.abs(openQty)) {
            // If opposite signs and old_qty is larger in magnitude, subtract
            updatedOldQty = this.old_qty + openQty;
        } else {
            // If openQty is larger in magnitude, old_qty is completely nullified
            updatedOldQty = 0;
        }

        if (updatedOldQty > 0) {
            m2m = (this.last_trade_price - this.close_price) * updatedOldQty;
        }
        else if (updatedOldQty < 0) {
            m2m = (this.close_price - this.last_trade_price) * Math.abs(updatedOldQty);
        }
        return m2m;
    }

    /**
     * Calculates mark-to-market value against average price
     * 
     * For positions with old_qty:
     * - If old_qty is positive, adjusts buy quantity by subtracting old_qty
     * - If old_qty is negative, adjusts sell quantity by subtracting abs(old_qty)
     * 
     * Calculates average buy/sell prices using:
     * buyPrice = (buy_amount - settlement_amount) / buyQty * trade_value
     * sellPrice = (sell_amount - settlement_amount) / sellQty * trade_value
     * 
     * For net long positions (openQty > 0):
     * m2m = (last_trade_price - buyPrice) * openQty
     * 
     * For net short positions (openQty < 0): 
     * m2m = (sellPrice - last_trade_price) * abs(openQty)
     * 
     * @returns The calculated mark-to-market value
     */
    calculateM2mAgainstAvgPrice() {
        let m2m = 0;
        let buyQty = this.buy_qty;
        let sellQty = Math.abs(this.sell_qty);
        let buyPrice = 0;
        let sellPrice = 0;
        let openQty = 0;

        if (this.old_qty) {
            if (this.old_qty > 0) {
                buyQty = this.buy_qty - this.old_qty;
            }
            else {
                sellQty = Math.abs(this.sell_qty) - Math.abs(this.old_qty);
            }
        }

        //openQty is the net qty of buy and sell qty
        openQty = buyQty - sellQty;


        /**
          * olqQty  buyQty  SellQty  openQty(new)  openQty(old + new) updatedOldQty updateNewOpenQty
          * 3       3       4        -1            2                  2             0
          * 1       0       1        -1            0                  0             0
          * -3      4       3        1            -2                 -2             0
          * 1       1       0        1             2                  1             1
          * 3       0       4        -4           -1                  0             -1
          * -3      4       0        4             1                  0             1
          * -3      0       3        -3           -6                  0             -6
          * 4       3       4        -1            3                  3             0
          * 4       3       9        -6           -2                  0             -2
          * 1       1       1         0           1                   1             1
          * 5       4       0         4           9                   5             4
          * -9      3       4        -1          -10                 -9             -1
          * 2
          */

        let updatedOldQty = 0;

        if (this.old_qty * openQty > 0) {
            // Both have the same sign, old quantity remains
            updatedOldQty = this.old_qty;
        } else if (Math.abs(this.old_qty) >= Math.abs(openQty)) {
            // If opposite signs and old_qty is larger in magnitude, subtract
            updatedOldQty = this.old_qty + openQty;
        } else {
            // If openQty is larger in magnitude, old_qty is completely nullified
            updatedOldQty = 0;
        }

        if (buyQty != 0) {
            buyPrice = (this.buy_amount - (this.old_qty > 0 ? this.settlement_amount : 0)) / (buyQty * this.trade_value);
        }

        if (sellQty != 0) {
            sellPrice = (this.sell_amount - (this.old_qty < 0 ? this.settlement_amount : 0)) / (sellQty * this.trade_value);
        }

        const updatedOpenQty = this.open_qty - updatedOldQty;

        if (updatedOpenQty > 0) {
            m2m = (this.last_trade_price - buyPrice) * updatedOpenQty;
        }
        else if (updatedOpenQty < 0) {
            m2m = (sellPrice - this.last_trade_price) * Math.abs(updatedOpenQty);
        }

        return m2m;
    }



    calculateBookedPnl() {
        let bookedPnl = 0;

        bookedPnl += this.calculateBookedPnlAgainstAvgPrice();

        if (this.old_qty) {
            bookedPnl += this.calculateBookedPnlAgainstClosePrice();
        }

        this.booked_pl = bookedPnl;
    }

    // Needed in future
    // calculateBookedPnlPercentage(){
    //     const denominator = this.buy_qty * (this.applyClosePrice?this.buy_price: this.close_price);
    //     let percentage = denominator ? this.booked_pl / denominator : 0;
    //     if(!percentage){
    //         percentage = Math.abs(percentage);
    //     }
    //     else{
    //         percentage = percentage * 100;
    //     }
    //     //this.dayPnlPercentage = percentage;
    // }


    /**
     * booked pnl against avg price
     * At backend when some qty is squared off , it is firstly deducted from intraday qty if available
     * if not available then it is deducted from old qty
     * Eg:
     * oldQy buyQty sellQty openQty(new) openQty(old + new)
     * 1       1       0       1               2
     * 2       1       2       -1              1
     */

    calculateBookedPnlAgainstAvgPrice() {
        let buyQty = this.buy_qty;
        let sellQty = Math.abs(this.sell_qty);
        let buyPrice = 0;
        let sellPrice = 0;

        if (this.old_qty) {
            if (this.old_qty > 0) {
                buyQty = this.buy_qty - this.old_qty;
            }
            else {
                sellQty = Math.abs(this.sell_qty) - Math.abs(this.old_qty);
            }
        }

        if (buyQty != 0) {
            buyPrice = (this.buy_amount - (this.old_qty > 0 ? this.settlement_amount : 0)) / (buyQty * this.trade_value);
        }

        if (sellQty != 0) {
            sellPrice = (this.sell_amount - (this.old_qty < 0 ? this.settlement_amount : 0)) / (sellQty * this.trade_value);
        }

        return Math.min(buyQty, sellQty) * (sellPrice - buyPrice) * this.trade_value;
    }

    /**
     * if old qty is present then 
     * first deduct old qty from buy qty or sell qty
     * then calculate booked pnl
     * calculate open qty and then calculate pnl for closed quantity
     * min of open qty and old qty is taken because 
     * Eg: -ve represents sell side and +ve represents buy side
     * oldQty buyQty sellQty openQty  min(openQty, oldQty) Explanation
     * 3       3       3      0       0                    square off is done for buy and sell qty, here old qty is not closed
     * 3       3       4      -1      1                    square off is done for buy and sell qty for 3 qty, and 1 qty is closed from old qty
     * -3      4       1      3       3                    square off is done for sell qty for 1 qty, and 3 qty is closed from old qty  
     * 5       5       2      3       3                    square off is done for buy and sell qty for 2 qty, and 3 qty is not used from old qty as qty is of buy side
    */

    calculateBookedPnlAgainstClosePrice() {

        let buyQty = this.buy_qty;
        let sellQty = Math.abs(this.sell_qty);
        let buyAvgPrice = this.buy_price;
        let sellAvgPrice = this.sell_price;

        if (this.old_qty > 0) {
            buyQty = this.buy_qty - this.old_qty;
            if (buyQty != 0) {
                buyAvgPrice = (this.buy_amount - this.settlement_amount) / (buyQty * this.trade_value);
            }
        }
        else if (this.old_qty < 0) {
            sellQty = Math.abs(this.sell_qty) - Math.abs(this.old_qty);
            if (sellQty != 0) {
                sellAvgPrice = (this.sell_amount - this.settlement_amount) / (sellQty * this.trade_value);
            }
        }

        let openQty = buyQty - sellQty;

        let avgPrice = 0;

        if (openQty > 0 && this.old_qty < 0) {
            avgPrice = this.close_price - buyAvgPrice;
        }
        else if (openQty < 0 && this.old_qty > 0) {
            avgPrice = sellAvgPrice - this.close_price;
        }

        return Math.min(Math.abs(openQty), Math.abs(this.old_qty)) * avgPrice * this.trade_value;
    }


    // Needed in future
    // calculateDayPnlPercentageDenominator(position: PositionsObjResponse){
    //     let val1 = 0;

    //     let buyAvgPrice = position.buy_qty ? ((position.buy_amount - position.settlement_amount) / position.buy_qty * this.trade_value) : 0;
    //     let sellAvgPrice = position.sell_qty ? ((position.sell_amount - position.settlement_amount) / Math.abs(position.sell_qty) * this.trade_value) : 0;

    //     if (position.buy_qty !== 0 && Math.abs(position.sell_qty) !== 0) {
    //         val1 = Math.min(position.buy_qty, Math.abs(position.sell_qty)) * position.buy_price + 
    //                Math.abs(position.open_qty) * (position.open_qty > 0 ? this.buy_price : this.sell_price);
    //     } else if (position.buy_qty !== 0 && Math.abs(position.sell_qty) === 0) {
    //         val1 = position.buy_price * position.buy_qty;
    //     } else {
    //         val1 = position.sell_price * Math.abs(position.sell_qty);
    //     }

    //     return val1;
    // }

    updateLTP(last_trade_price: number, close_price?: number) {
        const oldM2m = this.m2m;
        this['last_trade_price'] = last_trade_price;
        this.last_trade_price = last_trade_price;

        if (close_price) {
            this.close_price = close_price;
            this.change_in_price = last_trade_price - close_price;
            this.change_percent = (this.change_in_price / close_price) * 100;
        }
        let m2m = this.m2m;


        if (this.instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE && this.applyClosePriceForBpl) {
            this.calculateBookedPnl();
        }

        if (this.open_qty) {
            if (this.open_qty > 0) {
                m2m = (this.last_trade_price -
                    this.buy_price) * this.open_qty;
            } else {
                m2m = (this.sell_price -
                    this.last_trade_price) * Math.abs(this.open_qty);
            }
        }
        // According to RT-3690
        if (this.market_id === MarketKeywords.MCX) {
            m2m = m2m * this.trade_value;
        }

        this.m2m = m2m;
        this.net_profit_loss = this.m2m + this.booked_pl;
        /**
         * if applyClosePriceForMtm is true then dyapnl to be considered same as netpnl(for future and option separate handling is done below)
         * else calculate day m2m and day pnl with respect to close price
         */
        if (this.applyClosePriceForMtm) {
            this.day_m2m = m2m;
            this.dayPnl = this.net_profit_loss;
        }
        else {
            if (this.buy_qty > this.sell_qty) {
                this.day_m2m = close_price ? (last_trade_price - close_price) * this.open_qty : 0;
            }
            else if (this.buy_qty < this.sell_qty) {
                this.day_m2m = close_price ? (close_price - last_trade_price) * Math.abs(this.open_qty) : 0;
            }
            else {
                this.day_m2m = 0;
            }

            if (this.market_id === MarketKeywords.MCX) {
                this.day_m2m = this.day_m2m * this.trade_value;
            }

            this.dayPnl = this.day_m2m + this.booked_pl;
        }

        //this.calculateBookedPnlPercentage();

        // this.calculateDayPnlPercentage();

        let pnlPercentageDenominator = 0;

        if (this.buy_qty !== 0 && this.sell_qty !== 0) {
            pnlPercentageDenominator = Math.min(this.buy_qty, this.sell_qty) * this.buy_price + Math.abs(this.open_qty) * (this.open_qty > 0 ? this.buy_price : this.sell_price);
        }
        else if (this.buy_qty !== 0 && this.sell_qty === 0) {
            pnlPercentageDenominator = this.buy_qty * this.buy_price;
        }
        else if (this.buy_qty === 0 && this.sell_qty !== 0) {
            pnlPercentageDenominator = this.sell_qty * Math.abs(this.sell_price);
        }

        if (this.market_id === MarketKeywords.MCX) {
            pnlPercentageDenominator = pnlPercentageDenominator * this.trade_value;
        }

        this.net_profit_loss_percentage = pnlPercentageDenominator ? (this.net_profit_loss / pnlPercentageDenominator) * 100 : 0;

        //this.dayPnlPercentage = this.net_profit_loss_percentage;

        /**
         * calulating day pnl for future and option again if applyClosePriceForMtm is true
         */
        if (this.applyClosePriceForMtm && (this.instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE || this.instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION)) {
            this.getDayProfitLoss();
        }

        return oldM2m;
    }

}


export class MutualFund implements MutualFundDataResponse {
    isin = '';
    balanceUnits = 0;
    currentValue = 0;
    currentNav = 0;
    purchaseValue = 0;
    purchaseNav = 0;
    schemeName = '';
    pledgeable = false;
    totalUnits = 0;
    collateralUnits = 0;
    category = '';
    gainLoss = 0;
    broker = '';
    pledge_check = true;
    quantity_to_pledge = 0;
    error = false;
    hairCut = 0;
    scrip_precision = 2;

    constructor(mutualFundResponse: MutualFundDataResponse, broker?: string) {
        if (mutualFundResponse) {
            Object.keys(this).forEach(key => {
                if (mutualFundResponse[key] !== undefined) {
                    this[key] = mutualFundResponse[key];
                }
            });

            this.broker = broker ?? '';
        }
        this.quantity_to_pledge = Math.floor(this.totalUnits - this.collateralUnits);
    }

    get netPL(): number {
        return this.currentValue - this.purchaseValue;
    }

    get netPLPercentage(): number {
        return this.purchaseValue ? (this.netPL / this.purchaseValue) * 100 : 0;
    }

    get isPledgeable(): boolean {
        return this.pledgeable && this.broker === VendorType.shareIndia;
    }

    get availableUnitsForPledge(): number {
        return Math.floor(this.totalUnits - this.collateralUnits);
    }

    /**
     * Calculates the collateral value after applying haircut
     * 
     * The collateral value is determined by the current market value minus a haircut.
     * A haircut is a percentage deduction applied to the value of securities 
    */
    get calculatedCollateralValue(): number {
        const totalValue = this.collateralUnits * this.currentNav;
        const haircut = ((this.hairCut / Math.pow(10, this.scrip_precision)) * totalValue) / 100;
        return totalValue - haircut;
    }
}

export class MarginSummary implements MarginSummaryList {
    margin_percent = 0;
    mtm_percent = 0;
    mtm_level = 0;
    cash = 0;
    client_code = '';
    pay_in = 0;
    pay_out = 0;
    adhoc = 0;
    credit_for_sale = 0;
    option_sell_premium = 0;
    margin_utilized = 0;
    mtm_profit = 0;
    booked_profit = 0;
    cash_available = 0;
    mtm_loss = 0;
    booked_loss = 0;
    option_buy_premium = 0;
    collateral = 0;
    net_mtm = 0;
    net_booked_profit = 0;
    precision = 0;
    custom_component = 0;
    osp_credited = 0;
    delivery_sell_benefit = 0;
    deductions = 0;
    exposure = 0;
    delivery_margin = 0;
    span = 0;
    var = 0;
    elm = 0;
    withdrawable_margin = 0;
    total_margin_available = 0;
    mtf_margin_blocked = 0;
    ualgo_collateral = 0;
    transaction_charge = 0;
    net_buy_option_premium = 0;
    delivery_net_buy_value = 0;
    booked_and_mtm_loss = 0;
    excess_margin_over_collateral = 0;

    constructor(marginSummaryResponse: MarginSummaryResponse) {
        Object.keys(this).forEach(key => {
            this[key] = marginSummaryResponse[key];
        });

        if (!this.precision) {
            this.precision = 2;
        }

        if (Object.keys(marginSummaryResponse).length > 0) {
            //formula: delivery_sell_benefit + credit_for_sale RE-3172
            this.delivery_sell_benefit = this.delivery_sell_benefit + this.credit_for_sale;
            //formula: margin_utilized = margin_utilized + deductions + pay_out RE-3172
            this.margin_utilized = this.margin_utilized + this.deductions + this.pay_out;
        }
    }
}

export class MarketWatch implements MarketWatchTable {
    /**
     * Definition of Properties is important
     * as Interfaces dont exist on Runtime.
     */
    security_id = 0;
    symbol = '';
    symbol_id = 0;
    scrip_name = '';
    market_id = '';
    scripName = '';
    instrument_type = '';
    series = '';
    oi_qty = null;
    oi_value = null;
    oi_change = null;
    marketName = '';
    maturity_date = null;
    bid_qty = null;
    bid_price = null;
    ask_price = null;
    ask_qty = null;
    last_trade_price = null;
    last_trade_qty = null;
    last_trade_time = null;
    last_update_time = null;
    change_in_price = null;
    open_price = null;
    high_price = null;
    low_price = null;
    close_price = null;
    hi_52_wk = null;
    lo_52_wk = null;
    perChange = null;
    upper_ckt_limit = null;
    lower_ckt_limit = null;
    volume = null;
    value = null;
    trend = null;
    total_bid_qty = null;
    total_ask_qty = null;
    expiry = '';
    strike_price = null;
    option_type = '';
    isin = '';
    avg_trade_price = null;
    scrip_precision = 2;
    qty_precision = 0;
    maturity_yearmon = '';
    maturity_yearmon2 = '';
    contract_type = '';
    underlying_id = null;
    suspended = '';
    tick_size = 0;
    symbolId = null;
    market_lot = 1;
    isTickUp = null;
    isTickDown = null;
    is_index = false;
    expiry_date2 = '';
    trade_value = 1;
    asm_gsm_surveillance_indicator = 0;
    fo_banned_status = false;
    options_exists = true;
    indicative_close_price = null;
    regulatory_indicator: Array<string> = [];
    formatted_symbol = '';
    is_mtf_enabled = false;

    constructor(marketWatchResponse?: any, skipTimeAdjustment?: boolean) {
        if (marketWatchResponse) {
            if (!skipTimeAdjustment) {
                // Last trade time is coming +5.30 hours extra from mcl. So, same formula is used in web and exe.
                if (marketWatchResponse.last_trade_time) {
                    marketWatchResponse.last_trade_time -= GMT_IST_DIFFERENCE_SEC;
                }

                if (marketWatchResponse.last_update_time) {
                    marketWatchResponse.last_update_time -= GMT_IST_DIFFERENCE_SEC;
                }
            }

            if (marketWatchResponse.market_id === MarketKeywords.MCX) {
                marketWatchResponse.isin = null;
            }



            Object.keys(marketWatchResponse).forEach(key => {
                this[key] = marketWatchResponse[key];
            });

        }
    }

    checkOptionsExists(symbolSearchService: SymbolSearchService) {
        try {
            if (this.is_index) {
                this.options_exists = INDEXSYMBOL[this.symbol?.toUpperCase()] ? true : false;
            } else if (this.instrument_type !== SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION) {
                const symbol = this.symbol.substring(0, this.symbol.lastIndexOf(' ') !== -1 ? this.symbol.lastIndexOf(' ') : this.symbol.length);
                const symbols = symbolSearchService.advanceSearch(`${SpotDerivativeMap[this.market_id] ?? this.market_id} ${symbol}`,
                    SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION, FilterOptions.symbol, null);

                if (!symbols?.length) {
                    this.options_exists = false;
                }
            }
        } catch (e) {
            console.error(e);
        }
    }

    setQuotesData(quote: MarketWatchQuote | SingleIndexQuotes) {
        /**
         * In case of indices(data from scrip master), symbol id is not present
         * So, we are using market id and security id to match the quote
         */
        if (quote && (quote.symbol_id === this.symbol_id || (quote.market_id === this.market_id && quote.security_id === this.security_id))) {
            if (this.is_index) {
                quote = SharedUtils.indexToSymbolQuote(quote as SingleIndexQuotes);
            }
            if ((quote as MarketWatchQuote).last_trade_price &&
                ((quote as MarketWatchQuote).last_trade_price > (quote as MarketWatchQuote).close_price)) {
                this.isTickUp = !(this.isTickDown = false);
            } else if ((quote as MarketWatchQuote).last_trade_price &&
                ((quote as MarketWatchQuote).last_trade_price < (quote as MarketWatchQuote).close_price)) {
                this.isTickDown = !(this.isTickUp = false);
            }

            Object.keys(quote).forEach(key => {
                this[key] = quote[key];
                // Last trade time and Last Updated Time is coming +5.30 hours extra from mcl. So, same formula is used in web and exe.
                if (key && (key === 'last_trade_time' || key === 'last_update_time')) {
                    this[key] -= GMT_IST_DIFFERENCE_SEC;
                }
            });


            this.hi_52_wk = (quote['high_price'] && this.hi_52_wk) && (quote['high_price'] >= this.hi_52_wk) ? quote['high_price'] : this.hi_52_wk;
            this.lo_52_wk = (quote['low_price'] && this.lo_52_wk) && (quote['low_price'] <= this.lo_52_wk) ? quote['low_price'] : this.lo_52_wk;
            if (this.last_trade_price && this.close_price) {
                this.change_in_price = this.last_trade_price - this.close_price;
                this.perChange = ((this.change_in_price / this.close_price) * 100);
            }
        }
    }

    copyKeysFromObject(obj: any) {
        Object.keys(obj).forEach(key => {
            this[key] = obj[key];
        });
    }
}

export class SymbolRow implements SymbolRowRequest {
    symbol_id = 0;
    security_id = 0;
    market_id = '';
    is_index = false;
    scrip_precision = 2;
    constructor(symbolRowRequest?: SymbolRowRequest) {
        if (symbolRowRequest) {
            Object.keys(this).forEach(key => {
                this[key] = symbolRowRequest[key];
            });
            if (!symbolRowRequest.is_index) {
                this.is_index = false;
            }
            if (symbolRowRequest.instrument_type === MarketWatchSymbolType.Index) {
                this.is_index = true;
            }
        }
    }
}


// ################################
// ########| Order Struct |########
// ################################

export class Order implements PlaceOrderRequest {
    original_order_id = '';
    original_exchange_id = '';
    side = OrderMode.Buy;
    product_type = '';
    order_type = OrderType.limit;
    trigger_price = 0;
    lotsize = '';
    quantity = null;
    lots = '';
    price = null;
    disclosed_qty = 0;
    modify_time = null;
    stopLossPrice = 0;
    square_off = 0;
    holding_type = 'none';
    client_id = '';
    apply_lot = 0;
    symbol_id = 0;
    validity = null;
    clientList = null;
    master_order_id = '';
    bracket_cover_order_details: BOCODetailStruct;
    generated_order_id = '';
    boco_type: AdvanceOrder = AdvanceOrder.REGULAR;
    // Initializer Platoform type as web
    platform = CURRENT_PLATFORMTYPE;
    order_category = OrderCategory.single;
    pending_qty = 0;
    latest_quantity = 0;
    scheduled_time = 0;

    constructor(order: any) {
        Object.keys(this).forEach(key => {
            console.log(key, order[key]);
            if (order[key]) {
                this[key] = order[key];
            }
        });

        this.product_type = order['product'] || this.product_type;

        this.order_type = order['orderType'] || this.order_type;
        if (order['lots']) {
            this.lotsize = order['lots'];
        }

        this.modify_time = order.modified;
        this['validity'] = order['validity'];

        if (order['triggerPrice']) {
            this.trigger_price = +order['triggerPrice'];
        }
    }
}

class BOCOParentDetails {
    quantity = 0;
    price = 0;

    constructor(qty: number, price: number) {
        this.quantity = qty;
        this.price = price;
    }
}

class BOCOStopLossOrderDetails {
    price_type: BOCOPriceType;
    price = 0;
    stop_mode_type: BOCOStopModeTypes;
    // TODO: Null in case of Absolute
    trailing_ticks = 1;

    constructor(priceType: BOCOPriceType, price: number, stopModeType: BOCOStopModeTypes, trailingTicks: number) {
        this.price_type = priceType;
        this.price = price;
        this.stop_mode_type = stopModeType;
        this.trailing_ticks = trailingTicks;
    }
}

class BOCOTargetOrderDetails {
    price_type: BOCOPriceType;
    price = 0;

    constructor(priceType: BOCOPriceType, price: number) {
        this.price_type = priceType;
        this.price = price;
    }
}

export class BOCODetailStruct {
    parent_order_details: BOCOParentDetails;
    stop_loss_order_details: BOCOStopLossOrderDetails;
    target_order_details: BOCOTargetOrderDetails;
    order_state: BracketCoverOrderState = BracketCoverOrderState.OrderState_Parent;


    constructor(
        struct: BOCOInitInterface | undefined,
        order: Order
    ) {

        this.order_state = order.bracket_cover_order_details ?
            order.bracket_cover_order_details.order_state :
            BracketCoverOrderState.OrderState_Parent;

        this.parent_order_details = order.bracket_cover_order_details ?
            order.bracket_cover_order_details.parent_order_details :
            new BOCOParentDetails(
                +order.quantity,
                +order.price
            );

        if (this.order_state !== BracketCoverOrderState.OrderState_Parent) {
            return;
        }


        if (!struct) {
            return;
        }

        this.stop_loss_order_details = new BOCOStopLossOrderDetails(
            struct.stop_loss_price_type,
            struct.stop_loss_price,
            struct.stop_loss_type,
            struct.trailing_ticks
        );

        if (order.boco_type === AdvanceOrder.BO) {
            this.target_order_details = new BOCOTargetOrderDetails(
                struct.target_price_type,
                struct.target_price
            );
        }
    }
}

// export class SymbolSearchStruct implements SearchSymbolResults {
//     security_id = 0;
//     symbol = '';
//     market_id = '';
//     scrip_name = '';
//     series = '';
//     symbol_id = 0;
//     expiry? = '';
//     option_type? = '';
//     strike_price?= 0;
//     instrument_type = '';
//     lot_size = 0;
//     isSelected? = false;
// }

export class ErrorMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const invalidCtrl = (control && control.invalid && control.parent.touched);
        const invalidParent = (control && control.parent && (control.parent.hasError('notSame') || control.parent.hasError('pairError')));
        return (invalidCtrl || invalidParent) && control.touched;
    }
}


// ###########################################
// ########| Segement Settings Model |########
// ###########################################

export class ExchangeSegmentSettings {
    /**
     * Model Class of Exchange Settigns. All Segment related utilities should be added in this model.
     */
    settings: MarketSegmentSettingsResponse = null;
    aggrigatedOrderTypeValidities = {};

    constructor(settings: MarketSegmentSettingsResponse) {
        this.settings = settings;
        this._getAggrigatedValidity();
        console.log(this.aggrigatedOrderTypeValidities);
    }

    private _getAggrigatedValidity() {
        Object.values(this.settings).forEach(exchangeSetting => {
            Object.values(exchangeSetting).forEach(insrumentSetting => {
                Object.keys(insrumentSetting.order_validity).forEach(orderType => {
                    if (this.aggrigatedOrderTypeValidities[orderType]) {
                        this.aggrigatedOrderTypeValidities[orderType].concat(insrumentSetting.order_validity[orderType]);
                    } else {
                        this.aggrigatedOrderTypeValidities[orderType] = insrumentSetting.order_validity[orderType];
                    }
                });
            });
        });
    }
}



export class OrderDepth {

    depth: OrderDepthInterface[];
    scripPrecision = 2;
    data: OrderDepthResponseInterface;

    constructor(orderDepthReponse?: OrderDepthResponseInterface, marketData?: MarketWatch) {
        if (orderDepthReponse) {
            if (marketData.scrip_precision) {
                this.scripPrecision = marketData.scrip_precision;
            }
            this.depth = this.cleanDepth(orderDepthReponse);
        }
    }

    generateFakeDepth() {
        const depth = [];
        for (let i = 0; i < 5; i++) {
            depth.push(this.createObj([null, null, null, null, null, null]));
        }

        return depth;
    }

    cleanDepth(symbolDepth: OrderDepthResponseInterface) {
        let depth = [];
        if (symbolDepth.buy && symbolDepth.sell) {
            for (let i = 0; i < symbolDepth.buy.length; i++) {
                {
                    depth.push({
                        ask: symbolDepth.sell[i].price,
                        bid: symbolDepth.buy[i].price,
                        askMktVol: symbolDepth.sell[i].qty,
                        bidMktVol: symbolDepth.buy[i].qty,
                        numberOfAsks: symbolDepth.sell[i].no_of_order,
                        numberOfBids: symbolDepth.buy[i].no_of_order
                    });
                }
            }
        } else {
            depth = this.generateFakeDepth();
        }
        return depth;
    }

    createObj(depthArr: Array<string>) {
        return {
            ask: depthArr[0],
            bid: depthArr[1],
            askMktVol: depthArr[2],
            bidMktVol: depthArr[3],
            numberOfAsks: depthArr[4],
            numberOfBids: depthArr[5]
        };
    }
}

// ################################
// ########| Socket Struct |########
// ################################

export class SockDepth {
    depth = [];

    constructor(depth: SocketDepth) {
        const arr = [];
        for (let i = 0; i < 5; i++) {
            {
                arr.push(this.CreateObj([
                    depth.sell[i].price,
                    depth.buy[i].price,
                    depth.sell[i].qty,
                    depth.buy[i].qty,
                    depth.sell[i].no_of_order,
                    depth.buy[i].no_of_order
                ]));
            }
        }

        this.depth = arr;
    }

    CreateObj(depthArr) {
        return {
            ask: depthArr[0],
            bid: depthArr[1],
            askMktVol: depthArr[2],
            bidMktVol: depthArr[3],
            numberOfAsks: depthArr[4],
            numberOfBids: depthArr[5]
        };
    }
}


export class OrderMessages implements OrderMessagesInterface {
    modified = 0;
    order_status = '';
    order_mode = '';
    market_id = '';
    symbol = '';
    scrip_name = '';
    series = '';
    quantity = 0;
    price = 0;
    last_filled_price = 0;
    order_type = '';
    order_validity = '';
    primary_client_code = '';
    exchange_order_id = 0;
    instrument_type = '';
    order_category = '';
    scrip_precision = 2;
    client_order_id = '';
    formatted_symbol = '';
    
    constructor(orderData: OrderBookDataResponse) {

        Object.keys(this).forEach(key => {
            this[key] = orderData[key];
        });

        // this.modified = Utils._convertDateTime(orderData.modified);
        if (orderData.order_status === OrderStatus.filled) {
            this.price = orderData.last_filled_price;
        }

        if(orderData.order_status === OrderStatus.partially_filled){
            this.quantity = orderData.last_filled_qty;
            this.price = orderData.last_filled_price;
        }
    }
}
/**
* Throttler To add throttle over quote feed we collect feed in these throttle classes from quotestore
* time : to check whether the curtime is greater or equal to lastupdated quote time + our throttle time
*/

export class Throttler {
    time: number;
    _quoteStore: QuoteStoreService;
    constructor(time: number = 0, quoteStore: QuoteStoreService) {
        this.time = time;
        this._quoteStore = quoteStore;
    }

}


/**
* Holding Throttler workes different as we update through isin
* we store all the incoming quotes from feed which are pending into pendingQuoteUpdate
* quotesToUpdate: to store which quotes are to update
* lastUpdateMap: to store last updated time of that quote
* updation logic is different as feed comes from both nsecm and bsecm so we update which have high trade price or
* having last trade time difference greater than 1s
*/

export class HoldingThrottler extends Throttler {
    _holdingStore: HoldingStore;
    // map to store for isin for which feed come -> Isin as Key - SymbolId as SymbolId
    pendingQuoteUpdate: Map<string, number> = new Map();
    quotesToUpdate = new Map<string, number>();
    // Map to Store isin -> LastUpdatedTime
    lastUpdateMap = new Map<string, number>();


    constructor(time: number = 0, quoteStore: QuoteStoreService, holdingStore: HoldingStore) {
        super(time, quoteStore);
        this._holdingStore = holdingStore;
        this.listenFeed();
    }

    listenFeed() {
        this._quoteStore.getFeedSubject(QUOTE_UPDATE_TYPE.THROTTLER_LTP).subscribe(symbolId => {
            const isin = this._holdingStore.symbolIdToISIN.get(symbolId);
            const newQuote = this._quoteStore.getLTPAndClosePrice(symbolId);
            this._holdingStore.updateQuote(newQuote);
            if (!isin) {
                return;
            }
            if (this.pendingQuoteUpdate.has(isin)) {
                const quote = this._quoteStore.getLTPAndClosePrice(this.pendingQuoteUpdate.get(isin));
                // as feed comes for isin from both nsecm and bsecm store to update which has either time difference of 1s or
                // which have greater last trade price
                if (newQuote.last_trade_time - quote.last_trade_time > 1000 || newQuote.last_trade_price > quote.last_trade_price) {
                    this.pendingQuoteUpdate.set(isin, symbolId);
                }
            } else {
                this.pendingQuoteUpdate.set(isin, symbolId);
            }

        });

    }

}

/**
* Normal Throttler workes different as we update through symbolId
* we store all the incoming quotes from feed which are pending into pendingQuoteUpdate
* quotesToUpdate: to store which quotes are to update
* lastUpdateMap: to store last updated time of that quote
* quoteType: as NormalThrottler can be used for many types such as for quotes or index
*/

export class NormalThrottler extends Throttler {
    pendingQuoteUpdate: Set<number> = new Set<number>();
    subscribedSymbols = new Set<number>();
    quotesToUpdate = new Set<number>();
    // Map to Store SymbolId -> LastUpdatedTime
    lastUpdateMap = new Map<number, number>();
    quoteType: QUOTE_UPDATE_TYPE;


    constructor(time: number = 0, quoteStore: QuoteStoreService, quoteType: QUOTE_UPDATE_TYPE) {
        super(time, quoteStore);
        this.quoteType = quoteType;
        this.listenFeed();
    }

    listenFeed() {
        this._quoteStore.getFeedSubject(this.quoteType).subscribe(symbolId => {
            if (this.subscribedSymbols.has(symbolId)) {
                if (!this.pendingQuoteUpdate.has(symbolId)) {
                    this.pendingQuoteUpdate.add(symbolId);
                }
            }
        });

    }

}

export class UpdatedThrottler extends Throttler {
    pendingQuoteUpdate: Set<string> = new Set<string>();
    subscribedSymbols = new Set<string>();
    quotesToUpdate = new Set<string>();
    // Map to Store SymbolId -> LastUpdatedTime
    lastUpdateMap = new Map<string, number>();
    quoteType: QUOTE_UPDATE_TYPE;


    constructor(time: number = 0, quoteStore: QuoteStoreService, quoteType: QUOTE_UPDATE_TYPE) {
        super(time, quoteStore);
        this.quoteType = quoteType;
        this.listenFeed();
    }

    listenFeed() {
        this._quoteStore.getFeedSubject(this.quoteType).subscribe(key => {
            if (this.subscribedSymbols.has(key)) {
                if (!this.pendingQuoteUpdate.has(key)) {
                    this.pendingQuoteUpdate.add(key);
                }
            }
        });

    }

}
